// src/store/auth/actions.ts
import { Dispatch } from "redux";
import {
  loginApi,
  registerApi,
  RegisterDetails,
  logoutApi,
  verifyEmailApi,
  resendVerificationApi,
} from "../../services/authApi";
import { storeAccessToken } from "./storeToken";

// action type constants
export const LOGIN_REQUEST = "auth/LOGIN_REQUEST";
export const LOGIN_SUCCESS = "auth/LOGIN_SUCCESS";
export const LOGIN_FAILURE = "auth/LOGIN_FAILURE";

export const REGISTER_REQUEST = "auth/REGISTER_REQUEST";
export const REGISTER_SUCCESS = "auth/REGISTER_SUCCESS";
export const REGISTER_FAILURE = "auth/REGISTER_FAILURE";

export const VERIFY_EMAIL_REQUEST = "auth/VERIFY_EMAIL_REQUEST";
export const VERIFY_EMAIL_SUCCESS = "auth/VERIFY_EMAIL_SUCCESS";
export const VERIFY_EMAIL_FAILURE = "auth/VERIFY_EMAIL_FAILURE";

export const RESEND_VERIFY_REQUEST = "auth/RESEND_VERIFY_REQUEST";
export const RESEND_VERIFY_SUCCESS = "auth/RESEND_VERIFY_SUCCESS";
export const RESEND_VERIFY_FAILURE = "auth/RESEND_VERIFY_FAILURE";

export const LOGOUT = "auth/LOGOUT";

// Thrown by login when the account's email hasn't been verified yet.
export const EMAIL_NOT_VERIFIED = "EMAIL_NOT_VERIFIED";

// login
export const login =
  (email: string, password: string) => async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_REQUEST });
    try {
      const { data } = await loginApi(email, password);
      const { accessToken, refreshToken, user } = data;

      // Same rule as the mobile app: no session until the email is verified.
      if (user && user.isVerified === false) {
        const message = "Please verify your email to continue.";
        dispatch({ type: LOGIN_FAILURE, payload: message });
        throw Object.assign(new Error(message), {
          code: EMAIL_NOT_VERIFIED,
          email: user.email,
        });
      }

      // ✅ Correctly save access token + user
      storeAccessToken({
        access_token: accessToken,
        refresh_token: refreshToken,
        user,
      });

      dispatch({
        type: LOGIN_SUCCESS,
        payload: { user, token: accessToken, refreshToken },
      });

      return data;
    } catch (err: any) {
      dispatch({
        type: LOGIN_FAILURE,
        payload: err.response?.data?.message || err.message,
      });
      throw err;
    }
  };

// register
export const register =
  (name: string, email: string, password: string, details?: RegisterDetails) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: REGISTER_REQUEST });
    try {
      await registerApi(name, email, password, details);
      dispatch({ type: REGISTER_SUCCESS });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message;
      dispatch({ type: REGISTER_FAILURE, payload: message });
      // Let the form show why (e.g. the email is already registered).
      throw new Error(message);
    }
  };

// verify email
export const verifyEmail =
  (email: string, code: string) => async (dispatch: Dispatch) => {
    dispatch({ type: VERIFY_EMAIL_REQUEST });
    try {
      await verifyEmailApi(email, code);
      dispatch({ type: VERIFY_EMAIL_SUCCESS });
    } catch (err: any) {
      dispatch({
        type: VERIFY_EMAIL_FAILURE,
        payload: err.response?.data?.message || err.message,
      });
    }
  };

// resend verification
export const resendVerification =
  (email: string) => async (dispatch: Dispatch) => {
    dispatch({ type: RESEND_VERIFY_REQUEST });
    try {
      await resendVerificationApi(email);
      dispatch({ type: RESEND_VERIFY_SUCCESS });
    } catch (err: any) {
      dispatch({
        type: RESEND_VERIFY_FAILURE,
        payload: err.response?.data?.message || err.message,
      });
    }
  };

// logout
export const logout = () => async (dispatch: Dispatch) => {
  // Optimistic logout so UI updates immediately.
  localStorage.clear();
  dispatch({ type: LOGOUT });

  try {
    await logoutApi();
  } catch {
    // ignore server errors
  }
};
export const logoutUser = () => ({
  type: LOGOUT,
});
